import React from 'react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import InfiniteV2 from './InfiniteV2'
import DayDividerV2 from './DayDividerV2'

import {
  createTimelineV2,
  getCurrentTimelineV2,
  getShowableOldV2
} from './helpers'
import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import { useSocket } from '@SonarCX/providers/SocketProvider'
import { selectConvo } from '@SonarCX/slices/convo'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { selectDMs } from '@SonarCX/slices/dms'
import { useReadStatus } from '@SonarCX/providers/ReadStatusProvider'
import { useAddRemoveParticipant } from '@SonarCX/providers/AddRemoveParticipantProvider'
import { useTimelinePagination } from '@SonarCX/providers/TimelinePaginationProvider'
import { useOwner } from '@SonarCX/providers/OwnerProvider'
import { useVerification } from '@SonarCX/providers/VerificationProvider'
import { useSurvey } from '@SonarCX/providers/SurveyProvider'
import { fetchTags } from '@SonarCX/slices/tag'
import { addAlert } from '@SonarCX/slices/chatBoxSlice'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import useStyles from './styles'

const TimelineV2 = ({
  search,
  session,
  targetID,
  targetRef,
  isDirect,
  empty,
  isClosed,
  containerRef,
  filterNotes,
  viewingUser,
  viewing,
  names,
  typingNames,
  convoOverride,
  onlyShowDateSeparators = false
}) => {
  const dispatch = useAppDispatch()

  const { allUsers } = useUserAPI()
  const {
    api: { handleFetchResult }
  } = useSocket()
  const { allConversations } = useAppSelector(selectConvo)
  const tagsSelector = useAppSelector(storeState => storeState.tag)
  const { tags } = tagsSelector
  const { tasks: allTasks } = useTaskAPI()
  const { dms } = useAppSelector(selectDMs)
  const { convos } = useReadStatus()
  const { setEditSessionUsers } = useAddRemoveParticipant()
  const hasPermission = useAccess()
  const { getData, isDoneLoading } = useTimelinePagination()
  const { deleted: ownerDeleted } = useOwner()
  const { verified } = useVerification() ?? { verified: true }
  const { getSurveys } = useSurvey()
  const [collapsed, setCollapsed] = React.useState([])
  const classes = useStyles()

  const conversations = React.useMemo(
    () => (isDirect ? dms : allConversations),
    [isDirect, dms, allConversations]
  )

  const doneLoading = React.useMemo(() => {
    const id = conversations[session]?.owner_id ?? session
    return isDoneLoading(id)
  }, [conversations, session, isDoneLoading])

  const convo = React.useMemo(
    () =>
      conversations[session] ??
      convoOverride ?? {
        messages: [],
        notes: [],
        startDate: Date.now(),
        endDate: null
      },
    [conversations, session, convoOverride]
  )

  const surveys = React.useMemo(() => {
    if (isDirect) {
      return []
    }

    return getSurveys(session)
  }, [isDirect, getSurveys, session])

  const lastRead = React.useMemo(() => {
    if (!convo.session || !(convo.session in convos)) {
      return new Date(null)
    }

    if (convo.users?.length !== Object.keys(convos[convo.session])?.length) {
      return new Date(null)
    }

    const vals = Object.values(convos[convo.session])
    if (!vals.length) {
      return new Date(null)
    }

    const date = vals.reduce((a, b) => (a < b ? a : b))

    return date
  }, [convo, convos])

  const canViewNotes = React.useMemo(
    () => hasPermission(PERMS.NotesFull),
    [hasPermission]
  )

  const p2p = React.useMemo(() => {
    // removing blue ticks from p2p chats
    return !convo.isGroup && !convo.owner_id
  }, [convo])

  const allTags = React.useMemo(() => [...new Set(tags)], [tags])

  // Get owner
  const owner = React.useMemo(() => {
    const ownerID = convo.owner_id
    if (ownerID) {
      return allUsers[ownerID]
    }

    return null
  }, [convo.owner_id, allUsers])

  const extraTasks = React.useMemo(() => {
    if (!owner) {
      return []
    }

    const taskIDs = (owner.tasks ?? []).map(task => task.id)
    const tasks = []

    const foundIDs = []
    Object.values(allTasks).forEach(task => {
      const taskFound =
        taskIDs.includes(task.id) ||
        (task.concerning_user_id &&
          [owner.id, convo.owner_id].includes(task.concerning_user_id))

      // Valid if loaded and not in a session
      const isValid =
        Object.keys(task).length > 2 && (task.session_ids?.length ?? 0) === 0
      if (taskFound && isValid && !foundIDs.includes(task.id)) {
        tasks.push(['TASK', new Date(task.created_at), task])
        foundIDs.push(task.id)
      }
    })

    return tasks
  }, [allTasks, convo, owner])

  const { messages, notes, startDate, endDate } = convo

  const [oldTimelines, oldStarts, currentTimeline, hasMultipleSessions] =
    React.useMemo(
      () =>
        createTimelineV2({
          messages,
          notes,
          tasks: Object.values(allTasks).filter(t => !t.deleted_at),
          surveys,
          convo,
          viewing,
          viewingUser,
          canViewNotes,
          endDate,
          session,
          filterNotes,
          search,
          allTags,
          allUsers,
          classes,
          targetID,
          targetRef,
          lastRead,
          p2p,
          isDirect,
          onlyShowDateSeparators
        }),
      [
        messages,
        notes,
        allTasks,
        surveys,
        convo,
        viewing,
        viewingUser,
        canViewNotes,
        endDate,
        session,
        filterNotes,
        search,
        allTags,
        allUsers,
        classes,
        targetID,
        targetRef,
        lastRead,
        p2p,
        isDirect,
        onlyShowDateSeparators
      ]
    )

  const toggleCollapse = React.useCallback(
    id => () => {
      setCollapsed(old => {
        if (old.includes(id)) {
          return old.filter(x => x !== id)
        }

        return [...new Set([...old, id])]
      })
    },
    []
  )

  const combinedOld = React.useMemo(
    () => [...oldStarts, ...extraTasks].sort((a, b) => a[1] - b[1]),
    [oldStarts, extraTasks]
  )

  const showableOld = React.useMemo(
    () =>
      getShowableOldV2({
        allTags,
        classes,
        collapsed,
        combinedOld,
        oldTimelines,
        toggleCollapse,
        onlyShowDateSeparators
      }),
    [
      allTags,
      classes,
      collapsed,
      combinedOld,
      oldTimelines,
      toggleCollapse,
      onlyShowDateSeparators
    ]
  )

  // TODO: This is a hack to get the current timeline to show up only with date separators
  // for now it works since we dont actively use the conversations module, once we start using it
  // we need to find another way to show data in the session timeline route!
  const current = React.useMemo(
    () =>
      getCurrentTimelineV2({
        empty,
        doneLoading,
        hasMultipleSessions,
        isDirect,
        verified,
        allUsers,
        classes,
        convo,
        currentTimeline,
        ownerDeleted,
        session,
        setEditSessionUsers,
        startDate,
        typingNames,
        onlyShowDateSeparators
      }),
    [
      empty,
      doneLoading,
      hasMultipleSessions,
      isDirect,
      verified,
      allUsers,
      classes,
      convo,
      currentTimeline,
      ownerDeleted,
      session,
      setEditSessionUsers,
      startDate,
      typingNames,
      onlyShowDateSeparators
    ]
  )

  const fullTimeline = React.useMemo(() => {
    const full = []

    if (empty && isDirect && currentTimeline.length > 0) {
      full.push(
        <DayDividerV2
          key='day-div-start'
          date={currentTimeline[0].props?.task?.date ?? new Date(null)}
        />
      )
    }

    full.push(...showableOld)

    full.push(...current)

    return full
  }, [empty, isDirect, currentTimeline, showableOld, current])

  React.useEffect(() => {
    if (!tagsSelector.loaded && !tagsSelector.loading) {
      dispatch(fetchTags())
    }
  }, [tagsSelector, dispatch])

  React.useEffect(() => {
    let userName = ''
    if (names.length === 1) {
      userName = names[0]
    } else if (names.length === 2) {
      userName = names.join(' and ')
    } else {
      let moreThanThree = names.join(', ')
      const idx = moreThanThree.lastIndexOf(',')
      userName =
        moreThanThree.substr(0, idx) + ' and' + moreThanThree.substr(idx + 1)
    }

    const SINGULAR = 'is not available for Live Chat.'
    const PLURAL = 'are not available for Live Chat.'

    if (!isDirect && names.length !== 0 && !isClosed && verified) {
      dispatch(
        addAlert({
          message: [
            <>
              <span className='bold'>{userName}</span>{' '}
              {names.length === 1 ? SINGULAR : PLURAL}
            </>,
            'They will not be able to see any chat messages sent until they are'
          ],
          unique: 'offline'
        })
      )
    }
  }, [dispatch, isClosed, isDirect, names, verified])

  const hasOnlyWrapper = React.useMemo(() => {
    const sum = currentTimeline.length + fullTimeline.length
    return sum <= 1
  }, [currentTimeline.length, fullTimeline.length])

  return (
    <>
      {empty && isDirect && hasOnlyWrapper ? (
        <div
          key='no-recent'
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            fontStyle: 'italic'
          }}
        >
          No recent activity
        </div>
      ) : (
        <div>
          <InfiniteV2
            key={session}
            id={conversations[session]?.owner_id ?? session}
            containerRef={containerRef}
            getData={getData}
            onDataFetched={handleFetchResult}
          >
            {fullTimeline}
          </InfiniteV2>
        </div>
      )}
    </>
  )
}

export default TimelineV2
